import * as React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Button,
} from "@mui/material";
import { useI18n } from "../../i18n/I18nProvider";

type BenchmarkThreat = {
  key: string;
  label_en: string;
  label_de: string;
  percent: number;
};

const STATIC_THREAT_BENCHMARKS: BenchmarkThreat[] = [
  {
    key: "energy-blackout",
    label_en: "Energy / blackout disruption",
    label_de: "Energie- / Stromausfall",
    percent: 58,
  },
  {
    key: "cyber-it",
    label_en: "Cyber / IT failure",
    label_de: "Cyber- / IT-Ausfall",
    percent: 52,
  },
  {
    key: "extreme-weather",
    label_en: "Extreme weather / storm / heat",
    label_de: "Extremwetter / Sturm / Hitze",
    percent: 47,
  },
  {
    key: "communication-mobile",
    label_en: "Communication or mobile network failure",
    label_de: "Kommunikations- oder Mobilfunkausfall",
    percent: 41,
  },
  {
    key: "transport-mobility",
    label_en: "Transport / mobility disruption",
    label_de: "Transport- / Mobilitätsstörung",
    percent: 35,
  },
  {
    key: "supply-chain-shops",
    label_en: "Supply chain / shops disruption",
    label_de: "Lieferketten- / Geschäftsstörung",
    percent: 31,
  },
  {
    key: "water-supply",
    label_en: "Water supply disruption",
    label_de: "Störung der Wasserversorgung",
    percent: 25,
  },
  {
    key: "medical-help",
    label_en: "Medical help delayed",
    label_de: "Medizinische Hilfe verzögert",
    percent: 18,
  },
  {
    key: "public-security",
    label_en: "Public security / unrest",
    label_de: "Öffentliche Sicherheit / Unruhen",
    percent: 14,
  },
  {
    key: "fire-building",
    label_en: "Fire / building incident",
    label_de: "Brand- / Gebäudevorfall",
    percent: 9,
  },
];

export default function OthersSaidV2(props: {
  handlePageChange: (page: string) => void;
  nextPage?: string;
  backPage?: string;
}) {
  const {
    handlePageChange,
    nextPage = "VibeQuestions:aware",
    backPage = "AwareThreatWallScreen",
  } = props;
  const { lang } = useI18n();
  const isDE = lang === "de";

  /* ---------------- NAVIGATION ---------------- */

  function handleContinue() {
    handlePageChange(nextPage);
  }

  function handleBack() {
    handlePageChange(backPage);
  }

  /* ---------------- UI ---------------- */

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 48px)",
        width: "100%",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
        backgroundImage: `
          linear-gradient(
            rgba(8, 8, 8, 0.78),
            rgba(8, 8, 8, 0.82)
          ),
          url("/scenarioBgs/threats.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Paper
        sx={{
          width: { xs: "100%", sm: "min(88vw, 780px)", md: "min(78vw, 860px)" },
          borderRadius: 5,
          p: { xs: 4, sm: 6, md: 7 },
          border: "1px solid rgba(255,255,255,0.14)",
          background:
            "linear-gradient(180deg, rgba(49, 32, 32, 0.95), rgba(34, 21, 21, 0.94))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
          textAlign: "center",
        }}
      >
        <Stack spacing={4} alignItems="center">

          {/* TITLE */}
          <Typography
            sx={{
              color: "#fff",
              fontSize: { xs: "1.6rem", sm: "2rem", md: "2.3rem" },
              fontWeight: 800,
              letterSpacing: "0.08em",
              lineHeight: 1.3,
            }}
          >
            {isDE
              ? "Hier siehst du, was andere gesagt haben"
              : "Here you can see what others said"}
          </Typography>

          {/* SUBTEXT */}
          <Typography
            sx={{
              color: "rgba(255,255,255,0.8)",
              fontSize: { xs: "1rem", sm: "1.15rem" },
            }}
          >
            {isDE
              ? "Diese Referenzwerte zeigen, welche Bereiche in der Vorbereitung besonders häufig genannt werden."
              : "These benchmark values show which preparedness concerns are most commonly raised."}
          </Typography>

          {/* LIST */}
          <Stack spacing={1.5} sx={{ width: "100%", mt: 3 }}>
            {STATIC_THREAT_BENCHMARKS.map((threat) => (
              <Box
                key={threat.key}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto" },
                  gap: { xs: 1, sm: 2 },
                  alignItems: "center",
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Typography sx={{ color: "#fff" }}>
                  {isDE ? threat.label_de : threat.label_en}
                </Typography>

                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end">
                  <Box
                    sx={{
                      width: { xs: "100%", sm: 180 },
                      height: 8,
                      borderRadius: 999,
                      backgroundColor: "rgba(255,255,255,0.12)",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${threat.percent}%`,
                        height: "100%",
                        borderRadius: 999,
                        background: "linear-gradient(90deg, #b50d20, #f05969)",
                      }}
                    />
                  </Box>
                  <Typography sx={{ color: "#ff5a5a", fontWeight: 700 }}>
                    {threat.percent}%
                  </Typography>
                </Stack>
              </Box>
            ))}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{
                  mt: 1,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 800,
                  px: 3,
                  borderColor: "rgba(255,255,255,0.4)",
                  color: "#fff",
                  "&:hover": {
                    borderColor: "#fff",
                    backgroundColor: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                {isDE ? "Zurück" : "Back"}
              </Button>

              <Button
                variant="contained"
                onClick={handleContinue}
                sx={{
                  mt: 1,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 800,
                  px: 3,
                  bgcolor: "#ffffff",
                  color: "#111827",
                  "&:hover": {
                    bgcolor: "#f3f4f6",
                  },
                }}
              >
                {isDE ? "Weiter" : "Continue"}
              </Button>
            </Stack>
          </Stack>

        </Stack>
      </Paper>
    </Box>
  );
}
